import * as Sentry from '@sentry/react';

/** {@link ErrorDetails} contains information about problems encountered during an API request. */
export type ErrorDetails<Code = string> = {
  /** A specific string constant indicating the error type. */
  code: Code;
  /** A fallback message to show the user if we fail to transform it based on the code. */
  message: string;
  /** Specifies the field that errored, in "dot.notation[0].for.subfields" */
  path?: string;
  /** Additional information specific to a particular type of error, such as a limit value */
  extra?: Record<string, unknown>;
};

/** An error that occurred during a fetch.  Contains the response. */
export class FetchError extends Error {
  response;
  requestId;
  errors: Array<ErrorDetails | RewordedErrorDetails>;

  constructor(response: Response) {
    super(response.statusText || String(response.status));
    this.name = 'FetchError';
    this.response = response;
    this.requestId = response.headers.get('x-request-id');
    this.errors = [];
  }
}

/** An error from form submission, containing an array of validation error objects */
export class ValidationError extends FetchError {
  reported;

  constructor(errors: Array<ErrorDetails | RewordedErrorDetails>, response: Response) {
    super(response);
    const fields = errors.map((error) => error.path ?? 'top-level');
    this.message = `Form validation error on field(s): ${fields.join(', ')}\n${JSON.stringify(errors, null, 2)}`;
    this.name = 'ValidationError';
    this.errors = errors;
    this.reported = false;
  }

  markReported() {
    this.reported = true;
  }
}

export class TimeoutError extends FetchError {
  constructor(errors: Array<ErrorDetails | RewordedErrorDetails>, response: Response) {
    super(response);
    this.message = `Server timeout: ${JSON.stringify(errors)}`;
    this.name = 'TimeoutError';
    this.errors = errors;
  }
}

export type AuthErrorCode = 'ERR_UNAUTHORIZED' | 'ERR_2FA_REQUIRED' | 'ERR_2FA_RENEWAL_REQUIRED';

/** The user is not fully authenticated */
export class AuthenticationError extends FetchError {
  code: AuthErrorCode;

  constructor(error: ErrorDetails<AuthErrorCode>, response: Response) {
    super(response);
    this.message = 'Not logged in';
    this.name = 'AuthenticationError';
    this.code = error.code;
  }
}

/** The user is not authorized */
export class AuthorizationError extends FetchError {
  constructor(response: Response) {
    super(response);
    this.message = 'Forbidden';
    this.name = 'AuthorizationError';
  }
}

export class MissingResourceError extends FetchError {
  constructor(response: Response) {
    super(response);
    this.message = 'Not Found';
    this.name = 'MissingResourceError';
  }
}

/** 429 errors from the server */
export class RateLimitError extends FetchError {
  constructor(response: Response) {
    super(response);
    this.message = 'Too many requests.';
    this.name = 'RateLimitError';
  }
}

/** Whoops, something blew up on the server.  This is essentially a 500, Internal Server Error */
export class InternalServerError extends FetchError {
  constructor(errors: ErrorDetails[], response: Response) {
    super(response);
    this.name = 'InternalServerError';
    this.errors = errors;
  }
}

/**
 * The request never reached the api server. This is all the Fetch spec gives us for a transport-level
 * failure -- no route to the host, DNS failure, TLS failure, or a connection dropped mid-response --
 * so there is no status or body to convert, and the browser's own message ("Load failed" in Safari,
 * "Failed to fetch" in Chrome) says nothing about the request never having left the machine.
 */
export class NetworkError extends Error {
  /**
   * Kept off the message so that the error-tracking issue title stays stable across call sites;
   * {@link trackError} supplies a fixed fingerprint so that the occurrences group together.
   */
  url: string;

  constructor(url: string, options?: ErrorOptions) {
    super('Request never reached the server', options);
    this.name = 'NetworkError';
    this.url = url;
  }
}

export class MissingEntityError extends Error {
  entityId: string;

  constructor(entityId: string) {
    super();
    this.message = `Could not find ${entityId}`;
    this.name = 'MissingEntityError';
    this.entityId = entityId;
  }
}

/**
 * Try to get field-level messages from an error and set those errors on form fields.
 *
 * @param error An error from the server, hopefully a ValidationError
 * @param setError a function that will call `useForm` of react-hook-form.  Should return true if the error
 *                 is successfully mapped, false otherwise.
 *
 * @returns boolean  Were any errors handled? Might want to throw a global error if not
 */
export function handleServerFormError(
  error: unknown,
  setError: (errorDetails: ErrorDetails | RewordedErrorDetails) => boolean
) {
  let anyHandled = false;
  if (error instanceof ValidationError) {
    error.errors.forEach((e) => {
      const handled = setError(e);
      if (handled) {
        anyHandled = true;
      } else if (e.path) {
        trackError(new Error(`Error on field "${e.path}" fell through: ${e.message}`));
      } else {
        trackError(new Error(`Error without path fell through: ${e.message}`));
      }
    });
  } else if (error instanceof NetworkError) {
    // A request that never reached the server has no response to map onto form
    // fields. Show offline-aware copy instead of the Sentry-issue-title message,
    // and report the NetworkError itself so the occurrence lands in the grouped
    // issue. Reported here rather than via `raiseGlobalError`, whose
    // `Raised global error: <copy>` string capture would be a second, unfingerprinted
    // event for the same failure and fragment that group right back apart.
    //
    // Capture first: the alert blocks, so a user who closes the tab while it is up would
    // otherwise leave with nothing captured and nothing for the offline queue to persist.
    trackError(error);
    showGlobalError(describeNetworkError());
    return true;
  } else if (error instanceof Error) {
    raiseGlobalError(error.message);
    // I suppose raising a global error is a form of "handling" it...
    return true;
  }

  return anyHandled;
}

/**
 * User-facing copy for a {@link NetworkError} raised in place of form errors.
 * Mirrors the offline-aware split in the `Oops` screen's describeError, worded for a
 * form submission rather than a page that failed to load.
 */
function describeNetworkError() {
  return navigator.onLine ?
      'The request did not reach our servers. Check your connection, or try again in a moment.'
    : 'Your device is offline. Connect to the internet and try again.';
}

/**
 * Show the user a message about something that went generally wrong, and report it.
 *
 * Callers that already report a structured error should use {@link showGlobalError}
 * instead, so the same failure is not also captured as an unfingerprinted string.
 *
 * TODO: Eventually this should be better, maybe a toast or a banner.
 */
function raiseGlobalError(message: string) {
  trackError(`Raised global error: ${message}`);
  showGlobalError(message);
}

/**
 * Show the user a message about something that went generally wrong, without reporting it.
 *
 * TODO: Eventually this should be better, maybe a toast or a banner.
 */
function showGlobalError(message: string) {
  alert(message);
}

type ExclusiveEventHintOrCaptureContext = Parameters<typeof Sentry.captureException>[1];
/**
 * Captures an exception event and sends it to Sentry.
 *
 * @param error — The error to capture.
 * @param context — Optional additional data to attach to the Sentry event.
 */
export function trackError(error: unknown, context?: ExclusiveEventHintOrCaptureContext) {
  let ctx = context;
  if (error instanceof FetchError) {
    // If we got a FetchError, overrride the context provided.
    ctx = { extra: { requestId: error.requestId, errors: error.errors } };
  } else if (error instanceof NetworkError) {
    const isOnline = navigator.onLine;
    ctx = {
      extra: { url: error.url },
      tags: { online: isOnline },
      fingerprint: ['network-error', isOnline ? 'online' : 'offline'],
    };
  }
  Sentry.captureException(error, ctx);
}

/**
 * Captures a message event, for conditions worth knowing about that aren't errors, and sends it to Sentry.
 *
 * @param message — The message to capture.
 * @param context — Optional severity level or additional data to attach to the Sentry event.
 */
export function trackMessage(message: string, context?: Parameters<typeof Sentry.captureMessage>[1]) {
  Sentry.captureMessage(message, context);
}

/**
 * Records a value in a Sentry metrics distribution, for watching a quantity grow; thresholds on it are
 * Sentry alert rules, not code.
 */
export function trackDistribution(
  name: string,
  value: number,
  options?: Parameters<typeof Sentry.metrics.distribution>[2]
) {
  Sentry.metrics.distribution(name, value, options);
}

/**
 * Increments a Sentry metrics counter, for expected conditions whose rate matters but whose individual
 * occurrences aren't actionable; thresholds on it are Sentry alert rules, not code.
 */
export function trackCount(name: string, value?: number, options?: Parameters<typeof Sentry.metrics.count>[2]) {
  Sentry.metrics.count(name, value, options);
}

/**
 * {@link trackUnrewordedValidationError} filters out error details which have been reworded,
 * considering them to be expected validation errors.  Anything else is unexpected, and
 * needs to be surfaced to our error tracking system.
 *
 * @param error A {@link ValidationError} which might have un-reworded error details to track
 * @param errorTracker A callback with which to track the error
 * @returns true if the error was tracked, false if not
 */
export function trackUnrewordedValidationError(
  error: ValidationError,
  errorTracker: (ValidationError: ValidationError) => void
) {
  const errors: RewordedErrorDetails[] = error.errors;
  const unrewordedErrors = errors.filter((err) => !err.reworded);
  if (unrewordedErrors.length) {
    errorTracker(new ValidationError(unrewordedErrors, error.response));
    return true;
  }
  return false;
}

/**
 * {@link makeFriendlyMessage} is used to change the message of a validation error and mark the error as
 * reworded, so that it is not sent off to our error tracking service.
 *
 * @param errorDetails A single validation error which might need to be reworded.
 * @param message A new message to use instead of the server-provided message
 * @returns An {@link ErrorDetails} object with the new message and a `reworded: true` property.
 */
function makeFriendlyMessage(errorDetails: ErrorDetails, message: string): RewordedErrorDetails {
  return {
    ...errorDetails,
    reworded: true,
    message,
  };
}

export type RewordedErrorDetails<Code = string> = ErrorDetails<Code> & { reworded?: boolean };

export function wasReworded(errorDetails: ErrorDetails | RewordedErrorDetails): errorDetails is RewordedErrorDetails {
  return 'reworded' in errorDetails && errorDetails.reworded === true;
}

/**
 * {@link rewordValidationMessages} provides a means for the client to look at validation errors returned from the
 * api server and transform the message that will be shown to users, either to internationalize them (in the future),
 * or to re-word the message.  Any error message that is not reworded could be shown to the user and sent to
 * our error tracking service in case we should be handling it differently.
 *
 * @param err An error that might be a validation error or some other kind of error from the api server
 * @param reword A callback which is given an individual ErrorDetail, and should return a string message
 * for any errors that need to be reworded.  Any error details which do not have a string returned will be sent to
 * our error tracking service.
 * @param errorTracker A dependency-injection callback, will be called with unreworded errors, defaults
 * to our Sentry.io tracker.
 */
export function rewordValidationMessages(
  err: unknown,
  reword: (err: ErrorDetails) => string | undefined,
  errorTracker: (err: ValidationError) => void = trackError
): never {
  if (err instanceof ValidationError) {
    const errors: RewordedErrorDetails[] = err.errors.map((errorDetails) => {
      const newMessage = reword(errorDetails);
      if (newMessage) {
        return makeFriendlyMessage(errorDetails, newMessage);
      }
      return errorDetails;
    });

    const rewordedError = new ValidationError(errors, err.response);

    // If not all errors were reworded, send them to our error tracking service so we find out about them
    if (trackUnrewordedValidationError(rewordedError, errorTracker)) {
      rewordedError.markReported();
    }

    throw rewordedError;
  }

  // Throw original error if this wasn't a validation error
  throw err;
}
